import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lightning,
  ArrowRight,
  CheckCircle,
  Plug,
  ShoppingBag,
  Wrench,
  Sparkle,
  ArrowSquareOut,
  Heartbeat,
  Briefcase,
  ForkKnife,
  Mountains,
  Car,
  Drop,
  House,
  Toolbox,
  HouseLine,
  CloudLightning,
  Buildings,
} from "@phosphor-icons/react";
import { Slider } from "../ui/slider";
import { apiClient } from "../../lib/api";

const ICON_MAP = {
  heartbeat: Heartbeat,
  plug: Plug,
  briefcase: Briefcase,
  fork_knife: ForkKnife,
  mountains: Mountains,
  lightning: Lightning,
  car: Car,
  drop: Drop,
  house: House,
  toolbox: Toolbox,
  house_line: HouseLine,
  cloud_lightning: CloudLightning,
  buildings: Buildings,
};

export default function StepEcoFlowPicker({
  monthlyKwh,
  onContinueToLead,
  onProductSelect,
  selectedProductId,
}) {
  const [days, setDays] = useState(1);
  const [selectedUseCases, setSelectedUseCases] = useState([]);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const toggleCase = (id) => {
    setSelectedUseCases((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.post("/ecoflow/match", {
          monthly_kwh: monthlyKwh,
          backup_days_wanted: days,
          selected_use_cases: selectedUseCases,
        });
        if (!cancelled) setMatch(data);
      } catch (e) {
        if (!cancelled) toast.error("Could not load EcoFlow products");
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [monthlyKwh, days, selectedUseCases]);

  const useCases = match?.use_cases || [];
  const ml = match?.match_logic;

  return (
    <div data-testid="step-ecoflow" className="max-w-6xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
          <Plug size={14} weight="fill" className="text-emerald-600" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
            Backup environment selector · EcoFlow certified installer
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight">
          Build your backup environment.
        </h2>
        <p className="mt-3 text-slate-600 text-lg max-w-3xl mx-auto">
          Pick what you actually need to run during an outage. Our matcher correlates
          your selections to the right EcoFlow product capacity (Wh + watts + 240V) —
          accurate sizing, no over-spending.
        </p>
      </div>

      {/* Use case selector grid */}
      <div className="card-clean p-8 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h3 className="font-display text-xl font-semibold text-slate-900">
            What do you need to power?
          </h3>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Select all that apply
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {useCases.map((uc) => {
            const Icon = ICON_MAP[uc.icon] || Lightning;
            const checked = selectedUseCases.includes(uc.id);
            return (
              <button
                key={uc.id}
                type="button"
                onClick={() => toggleCase(uc.id)}
                data-testid={`use-case-${uc.id}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all flex gap-3 relative ${
                  checked
                    ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/15"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    checked ? "bg-white shadow-sm" : "bg-slate-50"
                  }`}
                >
                  <Icon
                    size={22}
                    weight="duotone"
                    style={{ color: checked ? uc.color : "#64748B" }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900 text-sm leading-tight">{uc.label}</div>
                    {uc.needs_240v && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">240V</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-snug line-clamp-2">{uc.description}</div>
                  <div className="text-[10px] text-slate-400 mt-1 font-mono">
                    {uc.continuous_w}W · {(uc.daily_wh / 1000).toFixed(1)} kWh/day
                  </div>
                </div>
                {checked && (
                  <CheckCircle size={18} weight="fill" className="text-emerald-500 absolute top-2 right-2" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Days slider + computed needs */}
      <div className="card-clean p-8 mb-6">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-700">Days of backup wanted</span>
              <span className="font-display text-2xl font-bold text-slate-950" data-testid="ecoflow-days-display">
                {days} {days === 1 ? "day" : "days"}
              </span>
            </div>
            <Slider
              data-testid="ecoflow-days-slider"
              value={[days]}
              min={1}
              max={5}
              step={1}
              onValueChange={(v) => setDays(v[0])}
            />
            <div className="flex justify-between text-[11px] text-slate-500 mt-2">
              <span>Power flicker</span>
              <span>Multi-day storm</span>
            </div>
          </div>

          {ml && (
            <div data-testid="ecoflow-computed-needs" className="grid grid-cols-2 gap-3">
              <Tile label="Peak load" value={`${Math.round(ml.peak_continuous_w)}W`} sub="Continuous AC" tone="amber" />
              <Tile label="Storage need" value={`${(ml.required_wh / 1000).toFixed(1)} kWh`} sub={`${days} day${days===1?'':'s'} of selected use`} tone="sky" />
              <Tile label="240V required" value={ml.needs_240v ? "Yes" : "No"} sub="From your selections" tone={ml.needs_240v ? "violet" : "emerald"} />
              <Tile label="Match coverage" value={`${ml.estimated_coverage_days}d`} sub="At your daily demand" tone="emerald" />
            </div>
          )}
        </div>
      </div>

      {loading && <div className="text-center text-slate-500 py-12">Matching products...</div>}

      {/* Recommendation */}
      {match && !loading && (
        <>
          {/* Tier rail with all products + match badge */}
          {match.all_products && (
            <div className="card-clean p-6 mb-6" data-testid="ecoflow-tier-rail">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                EcoFlow lineup — sized in order
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {match.all_products.map((p) => {
                  const isPrimary = p.id === match.primary?.id;
                  return (
                    <div
                      key={p.id}
                      className={`relative rounded-xl border-2 p-2 transition-all bg-white flex flex-col items-center ${
                        isPrimary ? "border-amber-500 ring-2 ring-amber-500/20 scale-105 shadow-md" : "border-slate-200 opacity-60"
                      }`}
                    >
                      {isPrimary && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-wider text-white bg-amber-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                          Match
                        </span>
                      )}
                      <img src={p.image_url} alt={p.name} className="h-12 sm:h-14 w-full object-contain" loading="lazy" />
                      <div className="text-[9px] sm:text-[10px] font-semibold text-slate-700 mt-1 text-center leading-tight truncate w-full">
                        {p.name.replace("EcoFlow ", "")}
                      </div>
                      <div className="text-[9px] text-slate-500 font-mono">{(p.capacity_wh / 1000).toFixed(1)}kWh</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Primary match */}
          <motion.div
            key={match.primary.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="ecoflow-primary-match"
            className={`rounded-3xl border-2 transition-all overflow-hidden mb-8 ${
              selectedProductId === match.primary.id
                ? "border-emerald-500 ring-4 ring-emerald-500/15 bg-gradient-to-br from-emerald-50 to-white"
                : "border-amber-500 bg-gradient-to-br from-amber-50 to-white"
            }`}
          >
            <div className="grid lg:grid-cols-5 gap-0">
              <div className="lg:col-span-2 bg-white p-8 flex items-center justify-center border-r border-slate-200">
                <img src={match.primary.image_url} alt={match.primary.name} className="max-h-64 object-contain" />
              </div>
              <div className="lg:col-span-3 p-8">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-white bg-amber-500 px-3 py-1 rounded-full">
                    ⚡ Recommended for you
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                    {match.primary.voltage}
                  </span>
                </div>
                <h3 className="font-display text-3xl font-bold text-slate-950">{match.primary.name}</h3>
                <p className="text-amber-700 font-semibold mt-1">{match.primary.tagline}</p>
                <p className="text-slate-600 mt-3 leading-relaxed">{match.primary.ideal_for}</p>

                <div className="grid grid-cols-4 gap-3 mt-5">
                  <Stat label="Capacity" value={`${(match.primary.capacity_wh / 1000).toFixed(1)} kWh`} />
                  <Stat label="Continuous" value={`${match.primary.ac_output_w}W`} />
                  <Stat label="Surge" value={`${match.primary.surge_w}W`} />
                  <Stat label="From" value={`$${match.primary.price_usd.toLocaleString()}`} />
                </div>

                <div className="mt-3 text-[11px] text-slate-500">
                  Expandable to {(match.primary.expandable_wh / 1000).toFixed(0)} kWh with extra batteries
                </div>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {match.primary.good_for_critical_loads.map((load) => (
                    <span key={load} className="text-[11px] font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full">
                      {load}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex gap-3 flex-wrap">
                  <button
                    type="button"
                    data-testid="ecoflow-pick-primary"
                    onClick={() => onProductSelect(match.primary)}
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                      selectedProductId === match.primary.id
                        ? "bg-emerald-500 text-white"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                  >
                    {selectedProductId === match.primary.id ? (
                      <><CheckCircle size={18} weight="fill" /> Selected</>
                    ) : (
                      <><Sparkle size={18} weight="fill" /> Pick this one</>
                    )}
                  </button>
                  <a
                    href={match.primary.shop_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ecoflow-shop-primary"
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    <ShoppingBag size={18} weight="duotone" /> View on shop
                    <ArrowSquareOut size={14} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Alternates */}
          {match.alternates && match.alternates.length > 0 && (
            <div className="mb-8">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">
                Or pick another tier
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {match.alternates.map((p) => (
                  <AlternateCard
                    key={p.id}
                    product={p}
                    selected={selectedProductId === p.id}
                    onSelect={() => onProductSelect(p)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="text-center mb-8">
            <a
              href="https://shop.starpower.network"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold text-sm"
            >
              Browse the full EcoFlow lineup on shop.starpower.network <ArrowSquareOut size={14} />
            </a>
          </div>
        </>
      )}

      <div className="flex justify-center">
        <button
          type="button"
          data-testid="ecoflow-continue-btn"
          onClick={onContinueToLead}
          className="btn-primary inline-flex items-center gap-2"
        >
          {selectedProductId ? "Send my pick to a specialist" : "Skip — go straight to assessment"}
          <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function Tile({ label, value, sub, tone = "sky" }) {
  const toneMap = {
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
    sky: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700" },
  };
  const t = toneMap[tone] || toneMap.sky;
  return (
    <div className={`${t.bg} ${t.border} border rounded-xl p-3`}>
      <div className={`text-[10px] uppercase tracking-wider font-bold ${t.text}`}>{label}</div>
      <div className="font-display text-xl font-bold text-slate-950 mt-1">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-lg font-bold text-slate-950 mt-0.5">{value}</div>
    </div>
  );
}

function AlternateCard({ product, selected, onSelect }) {
  return (
    <div
      data-testid={`ecoflow-alt-${product.id}`}
      className={`rounded-2xl border-2 p-5 bg-white transition-all flex gap-4 ${
        selected
          ? "border-emerald-500 ring-4 ring-emerald-500/15"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="w-20 h-20 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
        <img src={product.image_url} alt={product.name} className="max-h-16 object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 truncate">{product.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{product.tagline}</div>
        <div className="text-xs text-slate-700 mt-1 font-mono">
          {(product.capacity_wh / 1000).toFixed(1)} kWh · {product.ac_output_w}W · {product.voltage} · ${product.price_usd}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onSelect}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
              selected ? "bg-emerald-500 text-white" : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
          >
            {selected ? "Selected" : "Pick this"}
          </button>
          <a
            href={product.shop_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
          >
            Shop <ArrowSquareOut size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}
